import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LOCALE_PARAM } from '@aiscoreboard/contracts';

/**
 * The three languages the scoreboard is offered in.
 *
 * Kept deliberately small: a snooker club picks one and never touches it
 * again, so a long list would be clutter at the exact moment a player is
 * standing at the table waiting to start.
 */
export const LOCALES = ['en', 'de', 'zh'] as const;
export type Locale = (typeof LOCALES)[number];

/** What the switcher shows. Each label is written in its own language, because
 * somebody looking for German cannot be assumed to read the English word for it. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  de: 'Deutsch',
  zh: '中文',
};

const STORAGE_KEY = 'aiscoreboard.locale';

const en = {
  'youtube.title': 'Automatic YouTube broadcasts',
  'youtube.managedSettings': 'This camera is managed by YouTube. Change its channel and stream assignment in the YouTube section above.',
  'youtube.description': 'Connect a club channel once, assign a stream to each table, then start and end broadcasts from the scoreboard.',
  'youtube.operatorLogin': 'Sign in to manage club broadcasts',
  'youtube.operatorLogout': 'Sign out of club management',
  'youtube.logoutFailed': 'Signed out on this browser, but the server session could not be closed:',
  'youtube.operatorRemembered': 'Club management signs out after five minutes away from Cameras. Sign out before sharing the tablet.',
  'youtube.venue': 'Club',
  'youtube.selectVenue': 'Select a club (create one above if needed)',
  'youtube.createVenue': 'Create a club',
  'youtube.clubName': 'Club name',
  'youtube.connect': 'Connect / reconnect YouTube',
  'youtube.connected': 'Authorization returned successfully. Check the connected channel below.',
  'youtube.callbackError': 'YouTube authorization failed or was cancelled. Reconnect and select the channel that owns this camera’s saved stream key.',
  'youtube.error.streamKey': 'The saved stream key does not match this YouTube channel. Check the selected channel and the camera key; saving the same key again will not fix this.',
  'youtube.error.channel': 'This camera is linked to another YouTube channel. Select its existing channel.',
  'youtube.error.rtmp': 'The saved server URL does not match this YouTube stream. Check the camera’s streaming URL.',
  'youtube.error.active': 'This stream is already receiving video. Stop the current stream before connecting it.',
  'youtube.error.reserved': 'This stream key is assigned to another camera. Use a separate key for this camera.',
  'youtube.error.expired': 'Authorization expired or was revoked. Connect YouTube again.',
  'youtube.error.changed': 'Camera settings changed during authorization. Please try again.',
  'youtube.error.permission': 'YouTube denied access. Check the channel’s live-streaming permissions.',
  'youtube.error.quota': 'YouTube’s request limit has been reached. Please try again later.',
  'youtube.error.network': 'Could not reach YouTube. Please try again.',
  'license.title': 'Device license',
  'license.status.unlicensed': 'Not activated',
  'license.status.active': 'License active',
  'license.status.expired': 'License expired',
  'license.status.offline': 'License valid; waiting for device verification',
  'license.status.revoked': 'License revoked',
  'license.plan.streaming': 'Plan: Streaming',
  'license.plan.ai_streaming': 'Plan: AI referee + streaming',
  'license.expires': 'Expires',
  'license.remaining': '{n} days remaining',
  'match.licenseExpired': 'Streaming license expired. Renew it on the Cameras page.',
  'license.renew': 'To extend your license, contact:',
  'license.migration': 'Server license enforcement is disabled (migration mode).',
  'youtube.authorizeCamera': 'Authorize YouTube',
  'youtube.authorizeFirst': 'Authorize YouTube for this camera in Cameras before using automatic broadcasts.',
  'youtube.saveKeyFirst': 'Save the stream key below before connecting YouTube.',
  'youtube.unpairHint': 'Unbinding this tablet does not disconnect YouTube.',
  'youtube.notConfigured': 'YouTube integration is not configured on this server.',
  'youtube.ownerOnly': 'Only club owners can connect channels and change stream assignments.',
  'youtube.channel': 'YouTube channel',
  'youtube.selectChannel': 'Select a channel',
  'youtube.stream': 'Table stream / key',
  'youtube.selectStream': 'Select an existing stream',
  'youtube.matchesKey': 'matches the saved key',
  'youtube.privacy': 'New broadcast visibility',
  'youtube.private': 'Private',
  'youtube.unlisted': 'Unlisted',
  'youtube.public': 'Public',
  'youtube.mappingHint': 'Saving enables automatic management, but does not start a broadcast. Select this camera for the tablet, then use the scoreboard switch. Each simultaneous table needs its own stream.',
  'youtube.publicWarning': 'Future broadcasts from this table will be publicly visible on the channel.',
  'youtube.confirmPublic': 'Confirm public broadcasts',
  'youtube.disable': 'Disable automatic management',
  'youtube.stop': 'Stop / cancel broadcast',
  'youtube.watch': 'Open YouTube broadcast',
  'youtube.disconnect': 'Disconnect channel',
  'youtube.disconnectWarning': 'Stop broadcasts and disable their table assignments first. Disconnecting removes this club connection, not the YouTube channel.',
  'youtube.confirmDisconnect': 'Confirm disconnect',
  'youtube.claimDevice': 'Assign an existing Pi to this club',
  'youtube.claimHint': 'Enter the device token from the Pi config.json, not its YouTube stream key. Only an unowned device can be assigned this way. Do not send the token in chat.',
  'youtube.deviceToken': 'Pi device token',
  'youtube.invalidAuthorizationUrl': 'The server returned an invalid Google authorization address.',
  'youtube.selectCamera': 'Use this camera on this tablet',
  'youtube.loginRequired': 'Open Cameras and sign in as a club owner or platform administrator to pair this tablet. A player login or remembered camera does not grant camera access.',
  'youtube.noManagedDevice': 'This match has no selected YouTube-managed camera.',
  'youtube.status.disabled': 'Manual RTMP mode',
  'youtube.status.idle': 'Ready — no broadcast started',
  'youtube.status.preparing': 'Preparing YouTube broadcast',
  'youtube.status.waiting_for_stream': 'Waiting for YouTube to receive the stream',
  'youtube.status.live': 'Live on YouTube',
  'youtube.viewers': 'YouTube · {n} watching',
  'youtube.viewersHint': 'Current viewers, updated about every 30 seconds. If YouTube omits the count, 0 is shown; counts may be delayed or hidden.',
  'youtube.viewersError': 'Live viewer count is temporarily unavailable. Retrying automatically.',
  'youtube.status.stopping': 'Ending YouTube broadcast',
  'youtube.status.ended': 'Broadcast ended',
  'youtube.status.error': 'Broadcast needs attention',
  'youtube.status.reauthorization_required': 'Reconnect YouTube before starting',
  'app.tagline':
    'Intelligent scoreboard with live stream and automated scoring.',

  'home.match': 'Match',
  'home.matchPlayers': '2 players',
  'home.matchFrames': 'Up to 19 frames',
  'home.matchRated': 'Verified live results count towards rating',
  'home.matchBreak': 'Live scoring counts towards highest break',
  'home.matchTimed': 'A time limit can be set',
  'home.practice': 'Practice',
  'home.practicePlayers': '1 player',
  'home.practiceUnrated': 'Does not affect your rating',
  'home.practiceNoBreak': 'Does not count towards the highest break ranking',
  'home.practiceCoaching': 'Coaching and Challenges',
  'home.practiceChallengeRank': 'Counts towards the challenge ranking',
  'home.soon': 'soon',
  'home.start': 'Start',
  'home.language': 'Language',
  'camera.title': 'Cameras',
  'camera.loginRequired': 'Sign in to view your club’s cameras. Camera configuration requires a club owner or platform administrator.',
  'camera.adminRequired': 'Your player account does not have camera administrator permissions. Club operators can view their cameras; only owners can configure or pair them.',
  'camera.platformAdmin': 'Platform administrator: all cameras are accessible. Administrator access is audited.',
  'camera.noneAssigned': 'No cameras are assigned to your account.',
  'camera.noneAssignedHint': 'Ask your club administrator for access.',
  'camera.refresh': 'Refresh',
  'spectator.title': 'Watch matches',
  'spectator.buttonHint': 'Live scores and streams by club',
  'spectator.hint': 'Ongoing matches across clubs. Scores update automatically.',
  'spectator.refresh': 'Refresh',
  'spectator.failed': 'Unable to refresh matches. Displayed scores may be out of date.',
  'spectator.empty': 'No matches are currently in progress.',
  'spectator.unassigned': 'Other matches',
  'spectator.match': 'Match',
  'spectator.player': 'Player',
  'spectator.points': 'Points',
  'spectator.frames': 'Frames',
  'spectator.frame': 'Frame {n}',
  'spectator.minutes': '{n} min',
  'spectator.interval': 'Between frames',
  'spectator.watch': 'Watch live',
  'spectator.watchMatch': 'Watch {a} vs {b} live on YouTube',
  'camera.looking': 'Looking for cameras…',
  'camera.noneOnline': 'No cameras online',
  'camera.noneOnlineHint': 'Cameras appear here when the Pi camera agent is running',
  'camera.noneYet': 'No cameras yet. Add one below.',
  'camera.addCamera': 'Add camera',
  'camera.namePlaceholder': 'e.g. Table 1 Pi',
  'camera.create': 'Create',
  'camera.createFailed': 'Failed to create camera',
  'camera.loadFailed': 'Failed to load',
  'camera.save': 'Save',
  'camera.saving': 'Saving…',
  'camera.saveFailed': 'Save failed',
  'camera.rotateToken': 'Rotate token',
  'camera.rotateFailed': 'Rotate failed',
  'camera.delete': 'Delete',
  'camera.confirmDelete': 'Confirm delete',
  'camera.deleteFailed': 'Delete failed',
  'camera.bindMatch': 'Bind to match',
  'camera.noMatch': '— No match —',
  'camera.rtmpUrl': 'RTMP URL',
  'camera.streamKey': 'Stream key',
  'camera.keyStored': 'stored',
  'camera.keepCurrentKey': 'Leave blank to keep current key',
  'camera.pasteStreamKey': 'Paste YouTube stream key',
  'camera.matchControlsStreaming': 'Start and stop streaming with the switch on the match screen.',
  'camera.boundToTablet': 'Bound to this tablet',
  'camera.notBoundToTablet': 'Not bound to this tablet',
  'camera.lastSeen': 'last seen',
  'camera.bindTabletToCamera': 'Bind this tablet to this camera',
  'camera.tokenWarning': '⚠ Copy this token now — it will never be shown again',
  'camera.copy': 'Copy',
  'camera.copied': 'Copied',
  'camera.tokenHint': 'Paste this into',
  'camera.tokenHintSuffix': 'on your Pi5',
  'camera.tokenSaved': "I've saved it ✓",
  'camera.explainerPrefix': 'Each camera is a Raspberry Pi running',
  'camera.explainerSuffix':
    'Set YouTube credentials, then enable streaming to allow this camera to go live.',
  'camera.timeNever': 'never',
  'camera.timeJustNow': 'just now',
  'camera.timeMinutesAgo': '{n}m ago',
  'camera.timeHoursAgo': '{n}h ago',
  'camera.timeDaysAgo': '{n}d ago',

  'clock.toggle': 'Time limit',
  'clock.toggleHint': 'Starts with the match.',
  'clock.off': 'No time limit.',
  'clock.minutes': 'Minutes',
  'clock.less': 'Less time',
  'clock.more': 'More time',
  'clock.left': 'Left',
  'clock.up': 'Time up',
  'clock.limit': 'Time limit: {n} minutes',

  'leave.title': 'Leave the match?',
  'leave.message':
    'The match will be settled on the frames won so far — {result}. This cannot be undone.',
  'leave.resultWin': '{name} wins {a}\u2013{b}',
  'leave.resultDraw': 'a draw at {a}\u2013{b}',
  /* The button on the bar and the button that confirms the dialog carry the
     same words, because they are the same act: the first opens the question,
     the second answers it. "Leave" was the wrong word for either — it sounds
     like stepping away from a match that carries on without you, and what
     actually happens is that the match is settled and closed. */
  'leave.confirm': 'End match',
  'leave.practiceConfirm': 'End practice',
  'leave.practiceTitle': 'Leave practice?',
  'leave.practiceMessage': 'This session will not be saved.',

  'practice.notSaved': 'Not saved — you are playing as a guest',
  'practice.guestHint':
    'You are practising as a guest. Nothing is recorded: sign in to keep your statistics and appear on the boards.',
  'practice.title': 'Practice',
  'practice.player': 'Player',
  'practice.start': 'Start practice',
  'practice.starting': 'Starting…',
  'practice.saved': 'Session saved',
  'practice.points': 'Points',
  'practice.break': 'Break',
  'practice.best': 'Best',
  'practice.finish': 'Finish session',
  'practice.clockHint': 'Starts with the session.',
  'practice.startFailed': 'Could not start a session',
  'practice.saveFailed': 'Could not save the session',

  'ranking.globalTitle': 'Global rating',
  'ranking.button': 'Rankings',
  'ranking.buttonHint': 'Live-match ratings and highest breaks',
  'ranking.seeAll': 'See all rankings',
  'ranking.empty': 'Nobody has been rated yet.',
  'ranking.failed': 'Rankings are unavailable.',

  'common.back': 'Back',
  'common.loading': 'Loading…',
  'common.pullToRefresh': 'Pull down to refresh',
  'common.releaseToRefresh': 'Release to refresh',
  'common.cancel': 'Cancel',
  'common.settings': 'Settings',
  'common.signOut': 'Sign out',
  'common.signIn': 'Sign in or register',
  'common.fullscreenEnter': 'Fullscreen',
  'common.fullscreenExit': 'Exit full',
  'common.fullscreenExitNative': 'Press F11 / Esc',

  'seat.player': 'Player {n}',
  /* What an unclaimed seat is *called*, as opposed to `seat.player`, which is
     what the seat *is*. The card is headed "Player 1" because that is the
     position at the table; the name in it starts as "Guest 1" because that is
     who is standing there until somebody signs in or types over it. */
  'seat.guestNumbered': 'Guest {n}',
  'seat.guestName': 'Or play as a guest',
  'seat.guestHint': 'Guest games are not rated and do not count towards statistics.',
  'seat.guest': 'Guest',
  /* One person cannot play both sides, so an account offered to a seat it is
     already sitting in the other side of is refused — and told which side. */
  'seat.alreadySeated': 'This account is already signed in as Player {n}.',
  /* The settings screen. Reached from a seat card on the board, or on a phone
     that scanned the code the same screen offers. */
  'profile.title': 'Account settings',
  'profile.tabOverview': 'Overview',
  'profile.tabStats': 'Statistics',
  'profile.tabEdit': 'Edit profile',
  'profile.tabDanger': 'Close account',
  'profile.statsFailed': 'Could not load statistics',
  'profile.ranksFailed': 'Could not load rankings',
  'profile.rankings': 'Rankings',
  'profile.practice': 'Practice',
  'profile.phoneTitle': 'On your own phone',
  'profile.phoneHint':
    'Scan to open your account on your phone. You sign in there with your own password, so nobody can change your details from this board.',
  /* Every change asks for the password again: the board is a shared tablet,
     and being signed in on it proves only that somebody forgot to sign out. */
  'profile.gateHint': 'Changing anything on this account needs your password again.',
  'profile.password': 'Password',
  'profile.unlock': 'Unlock',
  'profile.checking': 'Checking…',
  'profile.wrongPassword': 'Wrong password',
  'profile.firstName': 'First name',
  'profile.lastName': 'Last name',
  'profile.nationality': 'Nationality',
  'profile.club': 'Club',
  'profile.noClub': 'No club',
  'profile.clubCountry': 'Club country',
  'profile.clubCity': 'Club city',
  'profile.avatar': 'Avatar',
  'profile.save': 'Save changes',
  'profile.saving': 'Saving…',
  'profile.saved': 'Saved.',
  'profile.saveFailed': 'Could not save',
  'profile.avatarFailed': 'Could not upload the avatar',
  'profile.dangerHint':
    'Closing your account signs you out everywhere and releases your player ID. Match results already recorded stay in place, but you will no longer appear on any leaderboard.',
  'profile.typeToConfirm': 'Type {word} to confirm',
  'profile.close': 'Close my account permanently',
  'profile.closing': 'Closing…',
  'profile.closeFailed': 'Could not close the account',

  'stat.rating': 'Rating',
  'stat.matchesPlayed': 'Matches played',
  'stat.matchesWon': 'Matches won',
  'stat.matchWinRate': 'Match win rate',
  'stat.framesPlayed': 'Frames played',
  'stat.framesWon': 'Frames won',
  'stat.frameWinRate': 'Frame win rate',
  'stat.pointsScored': 'Points scored',
  'stat.pointsConceded': 'Points conceded',
  'stat.pointsPerFrame': 'Points per frame',
  'stat.potSuccessRate': 'Pot success',
  'stat.highestBreak': 'Highest break',
  'stat.totalBreaks': 'Breaks made',
  'stat.averageBreak': 'Average break',
  'stat.fifties': 'Breaks of 50+',
  'stat.centuries': 'Centuries',
  'stat.foulsConceded': 'Fouls conceded',
  'stat.pointsLostToFouls': 'Points lost to fouls',
  'stat.sessions': 'Sessions',
  'stat.challengesAttempted': 'Challenges attempted',
  'stat.challengesPassed': 'Challenges passed',
  'stat.progressScore': 'Progress score',
  'stat.practicePoints': 'Practice points',
  'stat.practiceHighestBreak': 'Practice highest break',
  'seat.idLabel': 'ID:',
  'seat.statFrames': 'Frames',
  'seat.statWinRate': 'Win rate',
  'seat.statPotRate': 'Pot success',
  'seat.statHighBreak': 'Highest break',
  'seat.statAvgBreak': 'Average break',

  'handicap.toggle': 'Handicap',
  'reds.label': 'Red balls',
  'match.redsDecrease': 'Remove one remaining red',
  'scoreEntry.open': 'Add points to {name}',
  'scoreEntry.title': 'Add points · {name}',
  'scoreEntry.amount': 'Points to add',
  'scoreEntry.clear': 'Clear input',
  'scoreEntry.delete': 'Delete last digit',
  'scoreEntry.confirm': 'Add points',
  'scoreEntry.record': '{n} points added',
  'match.redsIncrease': 'Add one remaining red',
  'match.redsAdjustHint': 'Adjust reds on the table (0–15), without scoring. Undo colour clearance before restoring reds.',
  'reds.fewer': 'Fewer red balls',
  'reds.more': 'More red balls',
  'handicap.off': 'No handicap.',
  'handicap.hint': '{name} starts {points} ahead.',
  'handicap.gives': '{name} gives',
  'handicap.givesShort': 'gives',
  'handicap.less': 'Fewer points',
  'handicap.more': 'More points',

  'match.title': 'New match',
  'match.rated': 'Both seats are signed in. Rating requires verified live scoring and completion.',
  'match.unrated': 'Sign in to save personal statistics. Rating requires both players and verified live scoring.',
  'match.start': 'Start match',
  'match.starting': 'Starting…',

  'board.title': 'Leaderboards',
  'board.liveOnly': 'Rankings only include data recorded during a server-confirmed live broadcast. Non-live play and older records without verified live status do not count.',
  'board.rating': 'Rating',
  'board.ratingHint': 'Elo requires both players to be signed in and every scoring action and the result to be recorded live.',
  'board.highestBreak': 'Highest break',
  'board.highestBreakHint': 'Only the live-scored portion of a break counts. Non-live scoring is excluded.',
  'board.practice': 'Practice',
  'board.practiceHint': 'Practice has no verified live-broadcast link. Personal progress is saved but does not enter rankings.',
  'board.city': 'City',
  'board.country': 'Country',
  'board.global': 'Global',
  'board.clubCity': 'Club city',
  'board.needCountry': 'Choose a country to see this ranking.',
  'board.needCity': 'Choose a city to see this ranking.',
  'board.empty': 'Nobody has ranked here yet.',
  'board.failed': 'Could not load the leaderboard',
  'board.yourPlace': 'Your place',

  'frames.label': 'Frames',
  'frames.fewer': 'One frame fewer',
  'frames.more': 'One frame more',
  'frames.count': 'Number of frames',
  'frames.single': 'A single frame',
  'frames.firstTo': 'First to {n}',
  'frames.max': 'max {n}',

  'signin.close': 'Close',
  'signin.tabSignIn': 'Sign in',
  'signin.tabNew': 'Register',
  'signin.username': 'Username',
  'signin.usernameHint': 'Your first name, a #, and your eight-digit member number. We emailed it to you.',
  'signin.email': 'Email',
  'signin.emailHint': 'Where your username is sent. It is never shown to other players.',
  'signin.password': 'Password',
  'signin.signIn': 'Sign in',
  'signin.signingIn': 'Signing in…',
  'signin.firstName': 'First name',
  'signin.lastName': 'Last name',
  'signin.nationality': 'Nationality',
  'signin.club': 'Club (optional)',
  'signin.clubName': 'Club name',
  'signin.clubCountry': 'Club country',
  'signin.clubCity': 'Club city',
  'signin.passwordHint': 'At least 8 characters, with letters and digits.',
  'signin.confirmPassword': 'Confirm password',
  'signin.mismatch': 'The two passwords do not match.',
  'signin.avatar': 'Avatar (optional)',
  'signin.avatarType': 'That file is not an image',
  'signin.avatarSize': 'Avatar must be under 25 MB',

  'crop.title': 'Choose your avatar',
  'crop.hint': 'Drag to move, pinch or use the slider to zoom.',
  'crop.zoom': 'Zoom',
  'crop.cancel': 'Cancel',
  'crop.use': 'Use this',
  'crop.again': 'Adjust the crop',
  'crop.loading': 'Opening the photo…',
  'crop.failed': 'That image could not be opened. Try another photo.',
  'crop.failedHeic':
    'That iPhone photo (HEIC) could not be read. Re-save it as a JPEG, or set Camera → Formats to "Most Compatible".',
  'signin.create': 'Create account',
  'signin.creating': 'Creating…',
  'signin.failed': 'Something went wrong',

  'pair.description':
    'Scan this to sign in or register on your phone. The seat fills in by itself — no password gets typed on this screen.',
  'pair.alt': 'Sign-in pairing QR code',
  'pair.short': 'Scan to sign in on your phone',
  'pair.forSeat': 'Scan to sign in as {seat}',
  'pair.generating': 'Generating…',
  'pair.copy': 'Copy the link',
  'pair.copied': 'Link copied.',
  'pair.waiting': 'Waiting… the code lasts {n} minutes.',
  'pair.expired': 'That code has expired. Show a fresh one.',
  'pair.newCode': 'New code',
  'pair.failed': 'Could not create a code',
  'pair.retry': 'Try again',

  'join.title': 'Take your seat',
  'join.subtitle': 'Register or sign in here, and the scoreboard will seat you.',
  'join.account': 'Your account',
  'join.failed': 'Could not take the seat',
  'join.success': "You're in",
  'join.seated': '{name} has taken the seat. Look at the scoreboard — it has already updated.',
  'join.idHint':
    'That is your username. It signs you in anywhere, and it is in the email we just sent you.',
  'join.done': 'Done',

  'toggle.on': 'On',
  'toggle.off': 'Off',

  'ball.red': 'red',  'ball.yellow': 'yellow',
  'ball.green': 'green',
  'ball.brown': 'brown',
  'ball.blue': 'blue',
  'ball.pink': 'pink',
  'ball.black': 'black',
  'ball.color': 'colour',

  'match.live': 'Live',
  'match.offline': 'Offline',
  'match.reconnecting': 'Reconnecting',
  'match.aiReferee': 'AI referee',
  'match.aiUnsupported': 'This device or its license does not support AI refereeing',
  'match.aiDeviceChecking': 'Checking device support…',
  'match.aiDeviceCheckFailed': 'Unable to check device support; retrying…',
  'match.stream': 'Streaming',
  'match.cameraNotFound': 'Camera not found',
  'match.cameraStreamingNotReady': 'Configure the RTMP URL and stream key in camera settings first',
  'match.frames': 'Frames',
  'match.bestOf': 'Best of {n}',
  'match.high': 'High {n}',
  'match.atTable': 'At the table',
  'match.handicapVerb': 'gives',
  'match.handicapPoints': 'a {n}-point start',
  'match.highLabel': 'High break',
  'match.onLabel': 'Ball on',
  'match.gapLabel': 'Gap',
  'match.breakLabel': 'Current break',
  'match.remainingLabel': 'Remaining',
  'match.redsLabel': 'Reds',
  'match.break': 'Break {n}',
  'match.on': 'Ball on: {ball}',
  'match.remaining': 'Reds {reds} \u00b7 Available {points}',
  'match.exhausted': 'No points left on the table — end the frame.',
  'match.foulAndMiss': 'Foul and Miss',
  'match.foulPoints': 'Foul points',
  'match.resetOverlay': 'Reset overlay',
  'match.resume': 'Resume',
  'match.tablePreview': 'Table preview',
  'match.preShotReference': 'Pre-shot reference',
  'match.referenceUnavailable': 'Camera reference unavailable',
  'match.restoreInstruction': 'Scoring paused. Replace the balls using the pre-shot image, then resume.',
  'match.paused': 'Scoring paused',
  'match.pausedWaitingRespot': 'Paused — waiting for colour to be re-spotted',
  'match.switchPlayer': 'Switch player',
  /* The key on the board, where the row is four pills wide and the word has to
     share its pill with an icon. "Switch player" is the full instruction and
     it belongs on the phone, where there is a whole button for it; here the
     verb alone is unambiguous, because there is nothing else on a snooker
     board that could be switched. */
  'match.switchShort': 'Switch',
  'match.takeTable': 'Hand the table to {name}',
  'match.foul': 'Foul {n}',
  'match.foulLabel': 'Foul',
  /* The free ball, offered to whoever is at the table after a foul. Whether
     the incoming player is snookered on every ball on is a question about the
     table after the balls have stopped, so it is something the referee turns
     on rather than something the board can work out from the foul. */
  'match.freeBall': 'Free ball',
  'match.foulBackHint': '{name} plays again. The foul points remain awarded.',
  'match.freeBallAction': 'Tap to decide',
  'match.freeBallArmed': 'Free ball — nominate any ball',
  'match.freeBallWorth': 'A nominated ball scores {n}',
  'match.undo': 'Undo',
  'match.nothingToUndo': 'Nothing to undo',
  'match.frameTied': 'The scores are tied. Select the frame winner.',
  'match.saving': 'Saving…',
  'match.requestUnconfirmed': 'The request timed out and may have been saved. Refresh to check the score before repeating it.',
  'match.connectionUnconfirmed': 'The connection was interrupted. This action could not be confirmed after automatic retries. Refresh and check the score and player at the table before repeating it.',
  'match.endFrame': 'End frame',
  'match.whoWon': 'Who won the frame?',
  'match.remake': 'Replay frame',
  'match.remakeConfirm': 'Replay this frame? The scores are cleared and no frame is awarded.',
  'match.remakeGo': 'Replay it',
  'match.cancel': 'Cancel',
  'match.hasFrame': '{name} has the frame',
  'match.unavailable': 'That match is not available',
  'match.backToStart': 'Back to the start',
  'match.suggestions': 'AI suggestions ({n})',
  'match.accept': 'Accept',
  'match.reject': 'Reject',
  'match.suggestPot': 'Potted {ball}',
  'match.suggestPotMany': 'Potted {n} \u00d7 {ball}',
  'match.suggestMiss': 'No pot, turn over',
  'match.suggestFoul': 'Foul, {n} away',

  'frame.over': 'Frame {n} over',
  'frame.matchOver': 'Match over',
  'frame.savedTitle': 'Match saved',
  'frame.savedHint': 'This match was automatically closed after 20 minutes without scoring or match controls. Your score has been saved.',
  'frame.savedFrames': 'Frame {n} · Frames won {a}–{b}',
  'frame.resumeOffline': 'Continuing keeps streaming and AI off. Turn them on separately if needed.',
  'frame.resumeMatch': 'Continue match',
  'frame.takesFrame': '{name} takes the frame',
  'frame.winsMatch': '{name} wins the match',
  'frame.result': 'Frame {n}: {a}\u2013{b}',
  'frame.framesWord': 'frames',
  'frame.summary': 'Best of {n} \u00b7 first to {m}',
  'frame.newMatch': 'New match',
  'frame.startNext': 'Start frame {n}',

  'remote.caption': 'Remote',
  'remote.useAsRemote': 'Use phone as remote',
  'remote.hint': 'Scan to score this match from your phone',
  'remote.alt': 'Remote control QR code',
  'remote.drawing': 'Drawing the code…',
  'remote.failed': 'Could not draw the QR code',
  'remote.title': 'Score from your phone',
  'remote.close': 'Close',
  'remote.description':
    'Anyone can scan to score, undo and end frames in this match, including a guest referee. No sign-in is required. This panel closes after 30 seconds; a scanned control link remains valid for 12 hours. Share it only with people allowed to score.',
  'remote.qrFailed': 'The QR code could not be drawn: {error}',
  'remote.useLink': 'Use the link below instead — it works just as well.',
  'remote.loopback':
    'No network address was found, so this link only works on this device. Check that the server is running and that this machine is on the venue network.',
  'remote.notConnected':
    'This device is not connected to the server, so the other one may not be either.',
  'remote.connecting': 'Connecting…',
  'remote.scanAgain': 'Scan the code on the scoreboard again.',
  'remote.framesShort': 'Frames {a}\u2013{b}',
  'remote.connected': 'Connected',
  'remote.matchOver': 'The match is over.',
  'remote.waitingNextFrame': 'Waiting for the scoreboard to start the next frame.',
} satisfies Record<string, string>;

export type MessageKey = keyof typeof en;

const de: Record<MessageKey, string> = {
  'youtube.title': 'Automatische YouTube-Livestreams',
  'youtube.managedSettings': 'Diese Kamera wird über YouTube verwaltet. Kanal und Stream-Zuordnung oben im YouTube-Bereich ändern.',
  'youtube.description': 'Einmal den Vereinskanal verbinden, jedem Tisch einen Stream zuordnen und dann über die Anzeigetafel starten und beenden.',
  'youtube.operatorLogin': 'Für die Livestream-Verwaltung anmelden',
  'youtube.operatorLogout': 'Von der Vereinsverwaltung abmelden',
  'youtube.logoutFailed': 'In diesem Browser abgemeldet, aber die Serversitzung konnte nicht beendet werden:',
  'youtube.operatorRemembered': 'Die Vereinsverwaltung wird fünf Minuten nach dem Verlassen der Kameraseite abgemeldet. Vor der Weitergabe des Tablets abmelden.',
  'youtube.venue': 'Verein',
  'youtube.selectVenue': 'Verein auswählen (bei Bedarf oben erstellen)',
  'youtube.createVenue': 'Verein erstellen',
  'youtube.clubName': 'Vereinsname',
  'youtube.connect': 'YouTube verbinden / erneut autorisieren',
  'youtube.connected': 'Autorisierung erfolgreich zurückgekehrt. Bitte den verbundenen Kanal unten prüfen.',
  'youtube.callbackError': 'Die YouTube-Autorisierung ist fehlgeschlagen oder wurde abgebrochen. Erneut verbinden und den Kanal zum gespeicherten Streamschlüssel auswählen.',
  'youtube.error.streamKey': 'Der gespeicherte Streamschlüssel passt nicht zu diesem YouTube-Kanal. Kanal und Kameraschlüssel prüfen; erneutes Speichern desselben Schlüssels hilft nicht.',
  'youtube.error.channel': 'Diese Kamera ist mit einem anderen YouTube-Kanal verbunden. Den bisherigen Kanal auswählen.',
  'youtube.error.rtmp': 'Die gespeicherte Server-URL passt nicht zu diesem YouTube-Stream. Die Streaming-URL der Kamera prüfen.',
  'youtube.error.active': 'Dieser Stream empfängt bereits Video. Den laufenden Stream vor dem Verbinden stoppen.',
  'youtube.error.reserved': 'Dieser Streamschlüssel ist einer anderen Kamera zugeordnet. Für diese Kamera einen eigenen Schlüssel verwenden.',
  'youtube.error.expired': 'Die Autorisierung ist abgelaufen oder wurde widerrufen. YouTube erneut verbinden.',
  'youtube.error.changed': 'Die Kameraeinstellungen wurden während der Autorisierung geändert. Bitte erneut versuchen.',
  'youtube.error.permission': 'YouTube hat den Zugriff verweigert. Die Livestream-Berechtigung des Kanals prüfen.',
  'youtube.error.quota': 'Das Anfragelimit von YouTube wurde erreicht. Bitte später erneut versuchen.',
  'youtube.error.network': 'YouTube konnte nicht erreicht werden. Bitte erneut versuchen.',
  'license.title': 'Gerätelizenz',
  'license.status.unlicensed': 'Nicht aktiviert',
  'license.status.active': 'Lizenz aktiv',
  'license.status.expired': 'Lizenz abgelaufen',
  'license.status.offline': 'Lizenz gültig; Geräteprüfung ausstehend',
  'license.status.revoked': 'Lizenz widerrufen',
  'license.plan.streaming': 'Paket: Livestream',
  'license.plan.ai_streaming': 'Paket: KI-Schiedsrichter + Livestream',
  'license.expires': 'Gültig bis',
  'license.remaining': 'Noch {n} Tage',
  'match.licenseExpired': 'Livestream-Lizenz abgelaufen. Bitte auf der Seite Cameras verlängern.',
  'license.renew': 'Zur Verlängerung der Lizenz kontaktieren Sie:',
  'license.migration': 'Lizenzprüfung am Server deaktiviert (Migrationsmodus).',
  'youtube.authorizeCamera': 'YouTube autorisieren',
  'youtube.authorizeFirst': 'YouTube für diese Kamera unter Kameras autorisieren, bevor automatische Livestreams verwendet werden.',
  'youtube.saveKeyFirst': 'Vor dem Verbinden den Streamschlüssel unten speichern.',
  'youtube.unpairHint': 'Das Entkoppeln dieses Tablets trennt nicht die YouTube-Verbindung.',
  'youtube.notConfigured': 'Die YouTube-Anbindung ist auf diesem Server nicht konfiguriert.',
  'youtube.ownerOnly': 'Nur Vereinsinhaber können Kanäle verbinden und Streams zuordnen.',
  'youtube.channel': 'YouTube-Kanal',
  'youtube.selectChannel': 'Kanal auswählen',
  'youtube.stream': 'Stream / Schlüssel für diesen Tisch',
  'youtube.selectStream': 'Vorhandenen Stream auswählen',
  'youtube.matchesKey': 'passt zum gespeicherten Schlüssel',
  'youtube.privacy': 'Sichtbarkeit neuer Livestreams',
  'youtube.private': 'Privat',
  'youtube.unlisted': 'Nicht gelistet',
  'youtube.public': 'Öffentlich',
  'youtube.mappingHint': 'Speichern aktiviert die Verwaltung, startet aber keinen Livestream. Diese Kamera für das Tablet auswählen und den Schalter der Anzeigetafel nutzen. Gleichzeitige Tischübertragungen benötigen jeweils einen eigenen Stream.',
  'youtube.publicWarning': 'Künftige Livestreams dieses Tisches werden öffentlich auf dem Kanal sichtbar.',
  'youtube.confirmPublic': 'Öffentliche Livestreams bestätigen',
  'youtube.disable': 'Automatische Verwaltung deaktivieren',
  'youtube.stop': 'Livestream beenden / abbrechen',
  'youtube.watch': 'YouTube-Livestream öffnen',
  'youtube.disconnect': 'Kanal trennen',
  'youtube.disconnectWarning': 'Zuerst Livestreams beenden und Tischzuordnungen deaktivieren. Das Trennen entfernt diese Vereinsverbindung, nicht den YouTube-Kanal.',
  'youtube.confirmDisconnect': 'Trennen bestätigen',
  'youtube.claimDevice': 'Vorhandenen Pi diesem Verein zuordnen',
  'youtube.claimHint': 'Das Geräte-Token aus config.json des Pi eingeben, nicht den YouTube-Streamschlüssel. Nur Geräte ohne Besitzer können so zugeordnet werden. Das Token nicht im Chat senden.',
  'youtube.deviceToken': 'Pi-Geräte-Token',
  'youtube.invalidAuthorizationUrl': 'Der Server hat eine ungültige Google-Autorisierungsadresse zurückgegeben.',
  'youtube.selectCamera': 'Diese Kamera auf diesem Tablet verwenden',
  'youtube.loginRequired': 'Kameras öffnen und als Vereinsinhaber oder Plattformadministrator anmelden, um dieses Tablet zu koppeln. Eine Spieleranmeldung oder gemerkte Kamera gewährt keinen Kamerazugriff.',
  'youtube.noManagedDevice': 'Für diese Partie ist keine YouTube-verwaltete Kamera ausgewählt.',
  'youtube.status.disabled': 'Manueller RTMP-Modus',
  'youtube.status.idle': 'Bereit — kein Livestream gestartet',
  'youtube.status.preparing': 'YouTube-Livestream wird vorbereitet',
  'youtube.status.waiting_for_stream': 'Warten auf den Stream-Empfang bei YouTube',
  'youtube.status.live': 'Live auf YouTube',
  'youtube.viewers': 'YouTube · {n} Zuschauer',
  'youtube.viewersHint': 'Aktuelle Zuschauer, etwa alle 30 Sekunden aktualisiert. Fehlt die Zahl bei YouTube, wird 0 angezeigt; Angaben können verzögert oder ausgeblendet sein.',
  'youtube.viewersError': 'Zuschauerzahl vorübergehend nicht verfügbar. Wird automatisch erneut abgerufen.',
  'youtube.status.stopping': 'YouTube-Livestream wird beendet',
  'youtube.status.ended': 'Livestream beendet',
  'youtube.status.error': 'Livestream erfordert Aufmerksamkeit',
  'youtube.status.reauthorization_required': 'YouTube vor dem Start erneut verbinden',
  /* Kept to one line at the width the headline sits in, like the English and
     the Chinese. "KI-Schiedsrichter-Wertung" is the exact term the control
     bar uses, but it is a 25-character word: it wrapped the tagline onto two
     lines and made the top of the page look like a paragraph rather than a
     caption. "KI-Wertung" says the same thing here, where the sentence has
     already said what the board is. */
  'app.tagline': 'Intelligente Snooker-Anzeigetafel mit Livestream und automatischer Wertung.',

  'home.match': 'Partie',
  'home.matchPlayers': '2 Spieler',
  'home.matchFrames': 'Bis zu 19 Frames',
  'home.matchRated': 'Bestätigte Live-Ergebnisse zählen zur Wertung',
  'home.matchBreak': 'Live erfasste Punkte zählen zum höchsten Break',
  'home.matchTimed': 'Ein Zeitlimit kann gesetzt werden',
  'home.practice': 'Training',
  'home.practicePlayers': '1 Spieler',
  'home.practiceUnrated': 'Beeinflusst Ihre Wertung nicht',
  'home.practiceNoBreak': 'Zählt nicht für die Rangliste der höchsten Breaks',
  'home.practiceCoaching': 'Coaching und Aufgabenstellungen',
  'home.practiceChallengeRank': 'Zählt für die Aufgaben-Rangliste',
  'home.soon': 'bald',
  'home.start': 'Starten',
  'home.language': 'Sprache',
  'camera.title': 'Kameras',
  'camera.loginRequired': 'Anmelden, um die Kameras deines Vereins zu sehen. Konfiguration erfordert Vereinsinhaber- oder Plattformadministrator-Rechte.',
  'camera.adminRequired': 'Dein Spielerkonto hat keine Kamera-Administratorrechte. Vereinsoperatoren können Kameras ansehen; nur Inhaber können sie konfigurieren oder koppeln.',
  'camera.platformAdmin': 'Plattformadministrator: Zugriff auf alle Kameras. Administratorzugriffe werden protokolliert.',
  'camera.noneAssigned': 'Deinem Konto sind keine Kameras zugeordnet.',
  'camera.noneAssignedHint': 'Bitte deinen Vereinsadministrator um Zugriff.',
  'camera.refresh': 'Aktualisieren',
  'spectator.title': 'Zuschauen',
  'spectator.buttonHint': 'Spielstände und Livestreams nach Verein',
  'spectator.hint': 'Laufende Partien aller Vereine. Die Spielstände werden automatisch aktualisiert.',
  'spectator.refresh': 'Aktualisieren',
  'spectator.failed': 'Partien konnten nicht aktualisiert werden. Angezeigte Spielstände sind möglicherweise veraltet.',
  'spectator.empty': 'Zurzeit laufen keine Partien.',
  'spectator.unassigned': 'Weitere Partien',
  'spectator.match': 'Partie',
  'spectator.player': 'Spieler',
  'spectator.points': 'Punkte',
  'spectator.frames': 'Frames',
  'spectator.frame': 'Frame {n}',
  'spectator.minutes': '{n} Min.',
  'spectator.interval': 'Frame-Pause',
  'spectator.watch': 'Live ansehen',
  'spectator.watchMatch': '{a} gegen {b} live auf YouTube ansehen',
  'camera.looking': 'Suche nach Kameras…',
  'camera.noneOnline': 'Keine Kamera online',
  'camera.noneOnlineHint': 'Kameras erscheinen hier, wenn der Pi-Kamera-Agent läuft',
  'camera.noneYet': 'Noch keine Kameras. Fügen Sie unten eine hinzu.',
  'camera.addCamera': 'Kamera hinzufügen',
  'camera.namePlaceholder': 'z. B. Tisch 1 Pi',
  'camera.create': 'Erstellen',
  'camera.createFailed': 'Kamera konnte nicht erstellt werden',
  'camera.loadFailed': 'Laden fehlgeschlagen',
  'camera.save': 'Speichern',
  'camera.saving': 'Speichert…',
  'camera.saveFailed': 'Speichern fehlgeschlagen',
  'camera.rotateToken': 'Token rotieren',
  'camera.rotateFailed': 'Rotation fehlgeschlagen',
  'camera.delete': 'Löschen',
  'camera.confirmDelete': 'Löschen bestätigen',
  'camera.deleteFailed': 'Löschen fehlgeschlagen',
  'camera.bindMatch': 'An Partie binden',
  'camera.noMatch': '— Keine Partie —',
  'camera.rtmpUrl': 'RTMP-URL',
  'camera.streamKey': 'Stream-Key',
  'camera.keyStored': 'gespeichert',
  'camera.keepCurrentKey': 'Leer lassen, um den aktuellen Key zu behalten',
  'camera.pasteStreamKey': 'YouTube-Stream-Key einfügen',
  'camera.matchControlsStreaming': 'Den Livestream über den Schalter in der Spielansicht starten und stoppen.',
  'camera.boundToTablet': 'An dieses Tablet gebunden',
  'camera.notBoundToTablet': 'Nicht an dieses Tablet gebunden',
  'camera.lastSeen': 'zuletzt gesehen',
  'camera.bindTabletToCamera': 'Dieses Tablet an diese Kamera binden',
  'camera.tokenWarning': '⚠ Diesen Token jetzt kopieren — er wird nie wieder angezeigt',
  'camera.copy': 'Kopieren',
  'camera.copied': 'Kopiert',
  'camera.tokenHint': 'In',
  'camera.tokenHintSuffix': 'auf Ihrem Pi5 einfügen',
  'camera.tokenSaved': 'Gespeichert ✓',
  'camera.explainerPrefix': 'Jede Kamera ist ein Raspberry Pi mit',
  'camera.explainerSuffix':
    'YouTube-Zugangsdaten setzen und dann Streaming aktivieren, damit diese Kamera live senden darf.',
  'camera.timeNever': 'nie',
  'camera.timeJustNow': 'gerade eben',
  'camera.timeMinutesAgo': 'vor {n} Min.',
  'camera.timeHoursAgo': 'vor {n} Std.',
  'camera.timeDaysAgo': 'vor {n} Tg.',

  'clock.toggle': 'Zeitlimit',
  'clock.toggleHint': 'Läuft ab Partiebeginn.',
  'clock.off': 'Kein Zeitlimit.',
  'clock.minutes': 'Minuten',
  'clock.less': 'Weniger Zeit',
  'clock.more': 'Mehr Zeit',
  'clock.left': 'Rest',
  'clock.up': 'Zeit abgelaufen',
  'clock.limit': 'Zeitlimit: {n} Minuten',

  'leave.title': 'Partie verlassen?',
  'leave.message':
    'Die Partie wird nach den bisher gewonnenen Frames entschieden — {result}. Das lässt sich nicht rückgängig machen.',
  'leave.resultWin': '{name} gewinnt {a}\u2013{b}',
  'leave.resultDraw': 'unentschieden {a}\u2013{b}',
  'leave.confirm': 'Partie beenden',
  'leave.practiceConfirm': 'Training beenden',
  'leave.practiceTitle': 'Training verlassen?',
  'leave.practiceMessage': 'Diese Sitzung wird nicht gespeichert.',

  'practice.notSaved': 'Nicht gespeichert — Sie spielen als Gast',
  'practice.guestHint':
    'Sie trainieren als Gast. Nichts wird aufgezeichnet: Melden Sie sich an, um Ihre Statistik zu behalten und in den Ranglisten zu erscheinen.',
  'practice.title': 'Training',
  'practice.player': 'Spieler',
  'practice.start': 'Training starten',
  'practice.starting': 'Wird gestartet …',
  'practice.saved': 'Sitzung gespeichert',
  'practice.points': 'Punkte',
  'practice.break': 'Break',
  'practice.best': 'Bestes',
  'practice.finish': 'Sitzung beenden',
  'practice.clockHint': 'Läuft ab Sitzungsbeginn.',
  'practice.startFailed': 'Die Sitzung konnte nicht gestartet werden',
  'practice.saveFailed': 'Die Sitzung konnte nicht gespeichert werden',

  'ranking.globalTitle': 'Weltrangliste',
  'ranking.button': 'Ranglisten',
  'ranking.buttonHint': 'Live-Wertungen und höchste Breaks',
  'ranking.seeAll': 'Alle Ranglisten ansehen',
  'ranking.empty': 'Es wurde noch niemand gewertet.',
  'ranking.failed': 'Ranglisten sind nicht verfügbar.',

  'common.back': 'Zurück',
  'common.loading': 'Wird geladen …',
  'common.pullToRefresh': 'Zum Aktualisieren nach unten ziehen',
  'common.releaseToRefresh': 'Zum Aktualisieren loslassen',
  'common.cancel': 'Abbrechen',
  'common.settings': 'Einstellungen',
  'common.signOut': 'Abmelden',
  'common.signIn': 'Anmelden oder registrieren',
  'common.fullscreenEnter': 'Vollbild',
  'common.fullscreenExit': 'Vollbild aus',
  'common.fullscreenExitNative': 'F11 / Esc drücken',

  'seat.player': 'Spieler {n}',
  'seat.guestNumbered': 'Gast {n}',
  'seat.guestName': 'Oder als Gast spielen',
  'seat.guestHint': 'Gastpartien werden nicht gewertet und zählen nicht für die Statistik.',
  'seat.guest': 'Gast',
  'seat.alreadySeated': 'Dieses Konto ist bereits als Spieler {n} angemeldet.',
  'profile.title': 'Kontoeinstellungen',
  'profile.tabOverview': 'Übersicht',
  'profile.tabStats': 'Statistik',
  'profile.tabEdit': 'Profil bearbeiten',
  'profile.tabDanger': 'Konto schließen',
  'profile.statsFailed': 'Die Statistik konnte nicht geladen werden',
  'profile.ranksFailed': 'Die Platzierungen konnten nicht geladen werden',
  'profile.rankings': 'Platzierungen',
  'profile.practice': 'Training',
  'profile.phoneTitle': 'Auf dem eigenen Handy',
  'profile.phoneHint':
    'Scannen Sie den Code, um Ihr Konto auf dem Handy zu öffnen. Dort melden Sie sich mit Ihrem eigenen Passwort an, damit niemand Ihre Daten an diesem Board ändern kann.',
  'profile.gateHint': 'Für jede Änderung an diesem Konto wird das Passwort erneut gebraucht.',
  'profile.password': 'Passwort',
  'profile.unlock': 'Entsperren',
  'profile.checking': 'Wird geprüft …',
  'profile.wrongPassword': 'Falsches Passwort',
  'profile.firstName': 'Vorname',
  'profile.lastName': 'Nachname',
  'profile.nationality': 'Nationalität',
  'profile.club': 'Verein',
  'profile.noClub': 'Kein Verein',
  'profile.clubCountry': 'Land des Vereins',
  'profile.clubCity': 'Stadt des Vereins',
  'profile.avatar': 'Profilbild',
  'profile.save': 'Änderungen speichern',
  'profile.saving': 'Wird gespeichert …',
  'profile.saved': 'Gespeichert.',
  'profile.saveFailed': 'Speichern nicht möglich',
  'profile.avatarFailed': 'Das Profilbild konnte nicht hochgeladen werden',
  'profile.dangerHint':
    'Beim Schließen des Kontos werden Sie überall abgemeldet und Ihre Spielernummer wird freigegeben. Bereits erfasste Ergebnisse bleiben bestehen, Sie erscheinen aber in keiner Rangliste mehr.',
  'profile.typeToConfirm': 'Zum Bestätigen {word} eingeben',
  'profile.close': 'Konto endgültig schließen',
  'profile.closing': 'Wird geschlossen …',
  'profile.closeFailed': 'Das Konto konnte nicht geschlossen werden',

  'stat.rating': 'Wertung',
  'stat.matchesPlayed': 'Partien gespielt',
  'stat.matchesWon': 'Partien gewonnen',
  'stat.matchWinRate': 'Siegquote (Partien)',
  'stat.framesPlayed': 'Frames gespielt',
  'stat.framesWon': 'Frames gewonnen',
  'stat.frameWinRate': 'Siegquote (Frames)',
  'stat.pointsScored': 'Erzielte Punkte',
  'stat.pointsConceded': 'Gegenpunkte',
  'stat.pointsPerFrame': 'Punkte pro Frame',
  'stat.potSuccessRate': 'Lochquote',
  'stat.highestBreak': 'Höchstes Break',
  'stat.totalBreaks': 'Gespielte Breaks',
  'stat.averageBreak': 'Durchschnittsbreak',
  'stat.fifties': 'Breaks ab 50',
  'stat.centuries': 'Century-Breaks',
  'stat.foulsConceded': 'Begangene Fouls',
  'stat.pointsLostToFouls': 'Punkte durch Fouls verloren',
  'stat.sessions': 'Sitzungen',
  'stat.challengesAttempted': 'Aufgaben versucht',
  'stat.challengesPassed': 'Aufgaben bestanden',
  'stat.progressScore': 'Fortschrittswert',
  'stat.practicePoints': 'Trainingspunkte',
  'stat.practiceHighestBreak': 'Höchstes Break im Training',
  'seat.idLabel': 'ID:',
  'seat.statFrames': 'Frames',
  'seat.statWinRate': 'Siegquote',
  'seat.statPotRate': 'Lochquote',
  'seat.statHighBreak': 'Höchstes Break',
  'seat.statAvgBreak': 'Durchschnittsbreak',

  'handicap.toggle': 'Vorgabe',
  'reds.label': 'Rote Kugeln',
  'match.redsDecrease': 'Eine verbleibende Rote entfernen',
  'scoreEntry.open': 'Punkte für {name} hinzufügen',
  'scoreEntry.title': 'Punkte hinzufügen · {name}',
  'scoreEntry.amount': 'Zusätzliche Punkte',
  'scoreEntry.clear': 'Eingabe löschen',
  'scoreEntry.delete': 'Letzte Ziffer löschen',
  'scoreEntry.confirm': 'Hinzufügen',
  'scoreEntry.record': '{n} Punkte hinzugefügt',
  'match.redsIncrease': 'Eine verbleibende Rote hinzufügen',
  'match.redsAdjustHint': 'Rote auf dem Tisch anpassen (0–15), ohne Punkte. Vor dem Hinzufügen das Abräumen der Farben rückgängig machen.',
  'reds.fewer': 'Weniger rote Kugeln',
  'reds.more': 'Mehr rote Kugeln',
  'handicap.off': 'Ohne Vorgabe.',
  'handicap.hint': '{name} startet mit {points}.',
  'handicap.gives': '{name} gibt',
  'handicap.givesShort': 'gibt',
  'handicap.less': 'Weniger Punkte',
  'handicap.more': 'Mehr Punkte',

  'match.title': 'Neue Partie',
  'match.rated': 'Beide Plätze sind angemeldet. Die Wertung erfordert bestätigte Live-Erfassung bis zum Ergebnis.',
  'match.unrated':
    'Anmelden speichert persönliche Statistiken. Die Wertung erfordert beide Spieler und bestätigte Live-Erfassung.',
  'match.start': 'Partie starten',
  'match.starting': 'Wird gestartet …',

  'board.title': 'Ranglisten',
  'board.liveOnly': 'Ranglisten berücksichtigen nur Daten aus einem vom Server bestätigten Livestream. Spiele ohne Livestream und ältere Daten ohne Live-Nachweis zählen nicht.',
  'board.rating': 'Wertung',
  'board.ratingHint': 'Für Elo müssen beide Spieler angemeldet und alle Spielaktionen sowie das Ergebnis live erfasst sein.',
  'board.highestBreak': 'Höchstes Break',
  'board.highestBreakHint': 'Nur live erfasste Teile eines Breaks zählen. Punkte ohne Livestream sind ausgeschlossen.',
  'board.practice': 'Training',
  'board.practiceHint': 'Training hat keine bestätigte Livestream-Zuordnung. Persönlicher Fortschritt bleibt gespeichert, zählt aber nicht zur Rangliste.',
  'board.city': 'Stadt',
  'board.country': 'Land',
  'board.global': 'Weltweit',
  'board.clubCity': 'Stadt des Vereins',
  'board.needCountry': 'Wählen Sie ein Land, um diese Rangliste zu sehen.',
  'board.needCity': 'Wählen Sie eine Stadt, um diese Rangliste zu sehen.',
  'board.empty': 'Hier wurde noch niemand gewertet.',
  'board.failed': 'Die Rangliste konnte nicht geladen werden',
  'board.yourPlace': 'Ihre Platzierung',

  'frames.label': 'Frames',
  'frames.fewer': 'Ein Frame weniger',
  'frames.more': 'Ein Frame mehr',
  'frames.count': 'Anzahl der Frames',
  'frames.single': 'Ein einzelner Frame',
  'frames.firstTo': 'Wer zuerst {n} gewinnt',
  'frames.max': 'max. {n}',

  'signin.close': 'Schließen',
  'signin.tabSignIn': 'Anmelden',
  'signin.tabNew': 'Registrieren',
  'signin.username': 'Benutzername',
  'signin.usernameHint':
    'Ihr Vorname, ein # und Ihre achtstellige Mitgliedsnummer. Wir haben ihn Ihnen per E-Mail geschickt.',
  'signin.email': 'E-Mail',
  'signin.emailHint':
    'Dorthin geht Ihr Benutzername. Andere Spieler bekommen die Adresse nie zu sehen.',
  'signin.password': 'Passwort',
  'signin.signIn': 'Anmelden',
  'signin.signingIn': 'Wird angemeldet …',
  'signin.firstName': 'Vorname',
  'signin.lastName': 'Nachname',
  'signin.nationality': 'Staatsangehörigkeit',
  'signin.club': 'Verein (optional)',
  'signin.clubName': 'Vereinsname',
  'signin.clubCountry': 'Land des Vereins',
  'signin.clubCity': 'Stadt des Vereins',
  'signin.passwordHint': 'Mindestens 8 Zeichen, mit Buchstaben und Ziffern.',
  'signin.confirmPassword': 'Passwort bestätigen',
  'signin.mismatch': 'Die beiden Passwörter stimmen nicht überein.',
  'signin.avatar': 'Profilbild (optional)',
  'signin.avatarType': 'Diese Datei ist kein Bild',
  'signin.avatarSize': 'Das Profilbild darf höchstens 25 MB groß sein',

  'crop.title': 'Profilbild wählen',
  'crop.hint': 'Zum Verschieben ziehen, zum Zoomen zwei Finger oder den Regler.',
  'crop.zoom': 'Zoom',
  'crop.cancel': 'Abbrechen',
  'crop.use': 'Übernehmen',
  'crop.again': 'Ausschnitt ändern',
  'crop.loading': 'Foto wird geöffnet …',
  'crop.failed': 'Dieses Bild konnte nicht geöffnet werden. Versuchen Sie ein anderes Foto.',
  'crop.failedHeic':
    'Dieses iPhone-Foto (HEIC) ließ sich nicht lesen. Speichern Sie es als JPEG, oder stellen Sie Kamera → Formate auf „Maximale Kompatibilität".',
  'signin.create': 'Konto erstellen',
  'signin.creating': 'Wird erstellt …',
  'signin.failed': 'Da ist etwas schiefgegangen',

  'pair.description':
    'Scannen Sie dies, um sich auf dem Handy anzumelden oder zu registrieren. Der Platz füllt sich von selbst — auf diesem Bildschirm wird kein Passwort eingegeben.',
  'pair.alt': 'QR-Code zum Anmelden',
  'pair.short': 'Zum Anmelden mit dem Handy scannen',
  'pair.forSeat': 'Scannen, um als {seat} anzumelden',
  'pair.generating': 'Wird erzeugt …',
  'pair.copy': 'Link kopieren',
  'pair.copied': 'Link kopiert.',
  'pair.waiting': 'Warten … der Code gilt {n} Minuten.',
  'pair.expired': 'Dieser Code ist abgelaufen. Zeigen Sie einen neuen.',
  'pair.newCode': 'Neuer Code',
  'pair.failed': 'Der Code konnte nicht erstellt werden',
  'pair.retry': 'Erneut versuchen',

  'join.title': 'Nehmen Sie Ihren Platz ein',
  'join.subtitle': 'Registrieren oder melden Sie sich hier an, dann setzt die Anzeigetafel Sie ein.',
  'join.account': 'Ihr Konto',
  'join.failed': 'Der Platz konnte nicht übernommen werden',
  'join.success': 'Sie sind dabei',
  'join.seated':
    '{name} hat den Platz übernommen. Sehen Sie auf die Anzeigetafel — sie ist bereits aktualisiert.',
  'join.idHint':
    'Das ist Ihr Benutzername. Damit melden Sie sich überall an — er steht auch in der ' +
    'E-Mail, die wir Ihnen gerade geschickt haben.',
  'join.done': 'Fertig',

  'toggle.on': 'An',
  'toggle.off': 'Aus',

  'ball.red': 'Rot',
  'ball.yellow': 'Gelb',
  'ball.green': 'Grün',
  'ball.brown': 'Braun',
  'ball.blue': 'Blau',
  'ball.pink': 'Pink',
  'ball.black': 'Schwarz',
  'ball.color': 'Farbe',

  'match.live': 'Live',
  'match.offline': 'Offline',
  'match.reconnecting': 'Neu verbinden',
  'match.aiReferee': 'KI-Schiedsrichter',
  'match.aiUnsupported': 'Dieses Gerät oder seine Lizenz unterstützt keinen KI-Schiedsrichter',
  'match.aiDeviceChecking': 'Geräteunterstützung wird geprüft…',
  'match.aiDeviceCheckFailed': 'Geräteprüfung fehlgeschlagen; erneuter Versuch…',
  'match.stream': 'Streaming',
  'match.cameraNotFound': 'Kamera nicht gefunden',
  'match.cameraStreamingNotReady': 'Zuerst RTMP-URL und Stream-Key in den Kameraeinstellungen einrichten',
  'match.frames': 'Frames',
  'match.bestOf': 'Best of {n}',
  'match.high': 'Höchstes {n}',
  'match.atTable': 'Am Tisch',
  'match.handicapVerb': 'gibt',
  'match.handicapPoints': '{n} Punkte Vorgabe',
  'match.highLabel': 'Höchstes Break',
  'match.onLabel': 'Ball on',
  'match.gapLabel': 'Abstand',
  'match.breakLabel': 'Aktuelles Break',
  'match.remainingLabel': 'Verbleibend',
  'match.redsLabel': 'Rote',
  'match.break': 'Break {n}',
  'match.on': 'Ball on: {ball}',
  'match.remaining': 'Rote {reds} \u00b7 Verfügbar {points}',
  'match.exhausted': 'Keine Punkte mehr auf dem Tisch — beenden Sie den Frame.',
  'match.foulAndMiss': 'Foul und Miss',
  'match.foulPoints': 'Foulpunkte',
  'match.resetOverlay': 'Position überlagern',
  'match.resume': 'Fortsetzen',
  'match.tablePreview': 'Tischvorschau',
  'match.preShotReference': 'Bild vor dem Stoß',
  'match.referenceUnavailable': 'Kamerabild nicht verfügbar',
  'match.restoreInstruction': 'Wertung pausiert. Kugeln anhand des Bildes zurücksetzen, dann fortsetzen.',
  'match.paused': 'Wertung pausiert',
  'match.pausedWaitingRespot': 'Pausiert — warten auf das Wiederaufsetzen der Farbigen',
  'match.switchPlayer': 'Spieler wechseln',
  'match.switchShort': 'Wechsel',
  'match.takeTable': 'Tisch an {name} übergeben',
  'match.foul': 'Foul {n}',
  'match.foulLabel': 'Foul',
  'match.freeBall': 'Free ball',
  'match.foulBackHint': '{name} spielt erneut. Die Strafpunkte bleiben bestehen.',
  'match.freeBallAction': 'Per Tippen entscheiden',
  'match.freeBallArmed': 'Free ball — beliebige Kugel ansagen',
  'match.freeBallWorth': 'Eine angesagte Kugel zählt {n}',
  'match.undo': 'Undo',
  'match.nothingToUndo': 'Nichts zum Rückgängigmachen',
  'match.frameTied': 'Punktegleichstand. Bitte den Gewinner dieses Frames auswählen.',
  'match.saving': 'Wird gespeichert…',
  'match.requestUnconfirmed': 'Zeitüberschreitung. Die Aktion könnte gespeichert sein. Vor einer Wiederholung aktualisieren und den Spielstand prüfen.',
  'match.connectionUnconfirmed': 'Die Verbindung wurde unterbrochen. Die Aktion konnte auch nach automatischer Wiederholung nicht bestätigt werden. Vor einer erneuten Eingabe aktualisieren und Spielstand sowie Spieler am Tisch prüfen.',
  'match.endFrame': 'Frame beenden',
  'match.whoWon': 'Wer hat den Frame gewonnen?',
  'match.remake': 'Frame wiederholen',
  'match.remakeConfirm':
    'Diesen Frame wiederholen? Die Punkte werden gelöscht und kein Frame wird gewertet.',
  'match.remakeGo': 'Wiederholen',
  'match.cancel': 'Abbrechen',
  'match.hasFrame': '{name} hat den Frame',
  'match.unavailable': 'Diese Partie ist nicht verfügbar',
  'match.backToStart': 'Zurück zum Anfang',
  'match.suggestions': 'KI-Vorschläge ({n})',
  'match.accept': 'Übernehmen',
  'match.reject': 'Verwerfen',
  'match.suggestPot': '{ball} gelocht',
  'match.suggestPotMany': '{n} \u00d7 {ball} gelocht',
  'match.suggestMiss': 'Kein Treffer, Wechsel',
  'match.suggestFoul': 'Foul, {n} Punkte',

  'frame.over': 'Frame {n} beendet',
  'frame.matchOver': 'Partie beendet',
  'frame.savedTitle': 'Partie gespeichert',
  'frame.savedHint': 'Diese Partie wurde nach 20 Minuten ohne Wertung oder Spielbedienung automatisch geschlossen. Der Spielstand wurde gespeichert.',
  'frame.savedFrames': 'Frame {n} · Gewonnene Frames {a}–{b}',
  'frame.resumeOffline': 'Livestream und KI bleiben beim Fortsetzen aus. Bei Bedarf separat einschalten.',
  'frame.resumeMatch': 'Partie fortsetzen',
  'frame.takesFrame': '{name} gewinnt den Frame',
  'frame.winsMatch': '{name} gewinnt die Partie',
  'frame.result': 'Frame {n}: {a}\u2013{b}',
  'frame.framesWord': 'Frames',
  'frame.summary': 'Best of {n} \u00b7 zuerst {m}',
  'frame.newMatch': 'Neue Partie',
  'frame.startNext': 'Frame {n} starten',

  'remote.caption': 'Fernbedienung',
  'remote.useAsRemote': 'Handy als Fernbedienung',
  'remote.hint': 'Scannen, um diese Partie vom Handy aus zu werten',
  'remote.alt': 'QR-Code für die Fernbedienung',
  'remote.drawing': 'Code wird erstellt …',
  'remote.failed': 'QR-Code konnte nicht erstellt werden',
  'remote.title': 'Vom Handy aus werten',
  'remote.close': 'Schließen',
  'remote.description':
    'Jeder kann scannen und diese Partie werten, Aktionen rückgängig machen und Frames beenden, auch ein Gast-Schiedsrichter. Keine Anmeldung nötig. Dieses Fenster schließt nach 30 Sekunden; ein gescannter Steuerungslink gilt weiterhin 12 Stunden. Nur mit berechtigten Personen teilen.',
  'remote.qrFailed': 'Der QR-Code konnte nicht gezeichnet werden: {error}',
  'remote.useLink': 'Nutzen Sie stattdessen den Link darunter — er funktioniert genauso gut.',
  'remote.loopback':
    'Es wurde keine Netzwerkadresse gefunden, daher funktioniert dieser Link nur auf diesem Gerät. Prüfen Sie, ob der Server läuft und dieser Rechner im Netzwerk des Veranstaltungsorts ist.',
  'remote.notConnected':
    'Dieses Gerät ist nicht mit dem Server verbunden, das andere möglicherweise auch nicht.',
  'remote.connecting': 'Verbinden …',
  'remote.scanAgain': 'Scannen Sie den Code auf der Anzeigetafel erneut.',
  'remote.framesShort': 'Frames {a}\u2013{b}',
  'remote.connected': 'Verbunden',
  'remote.matchOver': 'Die Partie ist beendet.',
  'remote.waitingNextFrame': 'Warten, bis die Anzeigetafel den nächsten Frame startet.',
};

const zh: Record<MessageKey, string> = {
  'youtube.title': 'YouTube 自动直播',
  'youtube.managedSettings': '此摄像头由 YouTube 自动管理，请在上方 YouTube 区域修改频道和推流分配。',
  'youtube.description': '连接俱乐部频道，为每张球桌分配推流入口，之后通过记分板开启和结束直播。',
  'youtube.operatorLogin': '登录俱乐部直播管理',
  'youtube.operatorLogout': '退出俱乐部管理',
  'youtube.logoutFailed': '已退出此浏览器的管理登录，但未能关闭服务器会话：',
  'youtube.operatorRemembered': '离开相机页面五分钟后，俱乐部管理账号会自动退出。将平板交给他人前请退出。',
  'youtube.venue': '俱乐部',
  'youtube.selectVenue': '请选择俱乐部（如有需要，先在上方创建）',
  'youtube.createVenue': '创建俱乐部',
  'youtube.clubName': '俱乐部名称',
  'youtube.connect': '连接／重新授权 YouTube',
  'youtube.connected': '授权已成功返回，请确认下面显示的频道是否正确。',
  'youtube.callbackError': 'YouTube 授权失败或已取消。请重新连接，并选择此摄像头已保存 Key 所属的频道。',
  'youtube.error.streamKey': '已保存的直播 Key 与此 YouTube 频道不匹配。请确认频道和摄像头 Key；重复保存相同 Key 无法解决。',
  'youtube.error.channel': '此摄像头已绑定另一个 YouTube 频道，请选择原来的频道。',
  'youtube.error.rtmp': '已保存的服务器 URL 与此 YouTube 推流入口不匹配，请检查摄像头的推流 URL。',
  'youtube.error.active': '此推流入口正在接收视频，请先停止当前推流再连接。',
  'youtube.error.reserved': '此直播 Key 已分配给另一台摄像头，请为当前摄像头使用独立的 Key。',
  'youtube.error.expired': '授权已过期或被撤销，请重新连接 YouTube。',
  'youtube.error.changed': '授权过程中摄像头设置发生了变化，请重新尝试。',
  'youtube.error.permission': 'YouTube 拒绝了访问，请检查此频道是否具备直播权限。',
  'youtube.error.quota': '已达到 YouTube 请求限额，请稍后重试。',
  'youtube.error.network': '无法连接 YouTube，请重试。',
  'license.title': '设备授权',
  'license.status.unlicensed': '未激活',
  'license.status.active': '授权有效',
  'license.status.expired': '授权已到期',
  'license.status.offline': '授权未到期，等待设备验证',
  'license.status.revoked': '授权已撤销',
  'license.plan.streaming': '套餐：直播',
  'license.plan.ai_streaming': '套餐：AI 裁判＋直播',
  'license.expires': '到期时间',
  'license.remaining': '剩余 {n} 天',
  'match.licenseExpired': '直播授权已过期，请在 Cameras 页面延长授权。',
  'license.renew': '如需延长授权，请联系：',
  'license.migration': '服务器尚未启用授权限制（迁移模式）。',
  'youtube.authorizeCamera': '授权 YouTube',
  'youtube.authorizeFirst': '请先在摄像头页面为此设备授权 YouTube，再使用自动直播。',
  'youtube.saveKeyFirst': '请先在下方保存直播 Key，再连接 YouTube。',
  'youtube.unpairHint': '解绑此平板不会断开 YouTube 连接。',
  'youtube.notConfigured': '服务器尚未配置 YouTube 接入。',
  'youtube.ownerOnly': '只有俱乐部老板可以连接频道和修改推流分配。',
  'youtube.channel': 'YouTube 频道',
  'youtube.selectChannel': '选择频道',
  'youtube.stream': '球桌推流入口／密钥',
  'youtube.selectStream': '选择已有推流入口',
  'youtube.matchesKey': '与已保存的 Key 匹配',
  'youtube.privacy': '新直播的可见范围',
  'youtube.private': '私享',
  'youtube.unlisted': '不公开列出',
  'youtube.public': '公开',
  'youtube.mappingHint': '保存只启用自动管理，不会立即开播。请将此摄像头选为平板设备，再使用对战页开关。同时直播的不同球桌必须使用各自的推流入口。',
  'youtube.publicWarning': '此球桌之后开启的直播将在频道中公开可见。',
  'youtube.confirmPublic': '确认使用公开直播',
  'youtube.disable': '停用自动管理',
  'youtube.stop': '结束／取消直播',
  'youtube.watch': '打开 YouTube 直播',
  'youtube.disconnect': '断开频道',
  'youtube.disconnectWarning': '请先结束直播并停用球桌分配。此操作只移除本俱乐部的连接，不会删除 YouTube 频道。',
  'youtube.confirmDisconnect': '确认断开',
  'youtube.claimDevice': '将已有 Pi 归属到此俱乐部',
  'youtube.claimHint': '输入 Pi 的 config.json 中的设备 token，不是 YouTube Key。只能认领尚无归属的设备，请勿在聊天中发送 token。',
  'youtube.deviceToken': 'Pi 设备 token',
  'youtube.invalidAuthorizationUrl': '服务器返回的 Google 授权地址无效。',
  'youtube.selectCamera': '在这台平板上使用此摄像头',
  'youtube.loginRequired': '请打开摄像头页面，以俱乐部所有者或平台管理员身份登录并配对平板。球员登录或记住摄像头不授予摄像头权限。',
  'youtube.noManagedDevice': '此对局没有选定的 YouTube 自动管理摄像头。',
  'youtube.status.disabled': '手动 RTMP 模式',
  'youtube.status.idle': '已准备，尚未开启直播',
  'youtube.status.preparing': '正在准备 YouTube 直播',
  'youtube.status.waiting_for_stream': '等待 YouTube 接收推流',
  'youtube.status.live': 'YouTube 直播中',
  'youtube.viewers': 'YouTube · {n} 人正在观看',
  'youtube.viewersHint': '当前在线观看人数，约每 30 秒更新。YouTube 未返回人数时显示 0；人数可能延迟或被隐藏。',
  'youtube.viewersError': '在线观看人数暂时无法获取，正在自动重试。',
  'youtube.status.stopping': '正在结束 YouTube 直播',
  'youtube.status.ended': '直播已结束',
  'youtube.status.error': '直播需要处理',
  'youtube.status.reauthorization_required': '请重新授权 YouTube 后再开播',
  'app.tagline': '智能斯诺克记分板，支持直播和自动积分。',

  'home.match': '对战',
  'home.matchPlayers': '2 名球手',
  'home.matchFrames': '最多 19 局',
  'home.matchRated': '经确认的直播对局计入评级',
  'home.matchBreak': '直播期间的得分计入单杆排名',
  'home.matchTimed': '允许设置时间限制',
  'home.practice': '练球',
  'home.practicePlayers': '1 名球手',
  'home.practiceUnrated': '不计入个人评级',
  'home.practiceNoBreak': '不计入单杆最高分排名',
  'home.practiceCoaching': '教学与挑战球型',
  'home.practiceChallengeRank': '计入练习挑战球型的排名',
  'home.soon': '即将推出',
  'home.start': '开始',
  'home.language': '语言',
  'camera.title': '摄像头',
  'camera.loginRequired': '请登录以查看俱乐部摄像头。只有俱乐部所有者或平台管理员可以配置摄像头。',
  'camera.adminRequired': '球员账号不自动拥有摄像头管理权限。俱乐部操作员可以查看，只有所有者可以配置或配对。',
  'camera.platformAdmin': '平台管理员：可访问全部摄像头。管理员访问会被审计记录。',
  'camera.noneAssigned': '你的账号未分配摄像头。',
  'camera.noneAssignedHint': '请联系俱乐部管理员获取权限。',
  'camera.refresh': '刷新',
  'spectator.title': '观赛',
  'spectator.buttonHint': '按俱乐部查看实时比分和直播',
  'spectator.hint': '各俱乐部正在进行的比赛，比分会自动更新。',
  'spectator.refresh': '刷新',
  'spectator.failed': '比赛刷新失败，当前显示的比分可能已过时。',
  'spectator.empty': '当前没有正在进行的比赛。',
  'spectator.unassigned': '其他比赛',
  'spectator.match': '对局',
  'spectator.player': '选手',
  'spectator.points': '本局比分',
  'spectator.frames': '局分',
  'spectator.frame': '第 {n} 局',
  'spectator.minutes': '{n} 分钟',
  'spectator.interval': '局间休息',
  'spectator.watch': '观看直播',
  'spectator.watchMatch': '观看 {a} 对 {b} 的 YouTube 直播',
  'camera.looking': '正在查找摄像头…',
  'camera.noneOnline': '暂无在线摄像头',
  'camera.noneOnlineHint': '当 Pi 摄像头代理运行时，摄像头会显示在这里',
  'camera.noneYet': '还没有摄像头，请在下方添加。',
  'camera.addCamera': '添加摄像头',
  'camera.namePlaceholder': '例如：Table 1 Pi',
  'camera.create': '创建',
  'camera.createFailed': '创建摄像头失败',
  'camera.loadFailed': '加载失败',
  'camera.save': '保存',
  'camera.saving': '保存中…',
  'camera.saveFailed': '保存失败',
  'camera.rotateToken': '轮换令牌',
  'camera.rotateFailed': '轮换失败',
  'camera.delete': '删除',
  'camera.confirmDelete': '确认删除',
  'camera.deleteFailed': '删除失败',
  'camera.bindMatch': '绑定到比赛',
  'camera.noMatch': '— 不绑定比赛 —',
  'camera.rtmpUrl': 'RTMP 地址',
  'camera.streamKey': 'Stream Key',
  'camera.keyStored': '已保存',
  'camera.keepCurrentKey': '留空则保留当前 key',
  'camera.pasteStreamKey': '粘贴 YouTube stream key',
  'camera.matchControlsStreaming': '请在对局界面使用直播开关开启或关闭直播。',
  'camera.boundToTablet': '已绑定到此平板',
  'camera.notBoundToTablet': '未绑定到此平板',
  'camera.lastSeen': '最后在线',
  'camera.bindTabletToCamera': '将此平板绑定到该摄像头',
  'camera.tokenWarning': '⚠ 请现在复制此令牌——之后不会再次显示',
  'camera.copy': '复制',
  'camera.copied': '已复制',
  'camera.tokenHint': '请将它粘贴到 Pi5 的',
  'camera.tokenHintSuffix': '中',
  'camera.tokenSaved': '我已保存 ✓',
  'camera.explainerPrefix': '每个摄像头都是运行',
  'camera.explainerSuffix': '的 Raspberry Pi。设置好 YouTube 凭据后，启用直播即可推流。',
  'camera.timeNever': '从未',
  'camera.timeJustNow': '刚刚',
  'camera.timeMinutesAgo': '{n} 分钟前',
  'camera.timeHoursAgo': '{n} 小时前',
  'camera.timeDaysAgo': '{n} 天前',

  'clock.toggle': '时间限制',
  'clock.toggleHint': '比赛开始即计时。',
  'clock.off': '不设时间限制。',
  'clock.minutes': '分钟',
  'clock.less': '减少时间',
  'clock.more': '增加时间',
  'clock.left': '剩余',
  'clock.up': '时间到',
  'clock.limit': '时间限制：{n} 分钟',

  'leave.title': '确定离开比赛？',
  'leave.message': '将按目前已赢下的局数判定结果：{result}。此操作无法撤销。',
  'leave.resultWin': '{name} 以 {a}\u2013{b} 获胜',
  'leave.resultDraw': '{a}\u2013{b} 战平',
  'leave.confirm': '结束比赛',
  'leave.practiceConfirm': '结束练球',
  'leave.practiceTitle': '确定离开练球？',
  'leave.practiceMessage': '本次练习不会被保存。',

  'practice.notSaved': '未保存——当前为访客身份',
  'practice.guestHint': '你正在以访客身份练球，成绩不会被记录。登录后即可保存技术统计并进入排行榜。',
  'practice.title': '练球',
  'practice.player': '球手',
  'practice.start': '开始练球',
  'practice.starting': '正在开始…',
  'practice.saved': '本次练习已保存',
  'practice.points': '总得分',
  'practice.break': '当前单杆',
  'practice.best': '最高单杆',
  'practice.finish': '结束练球',
  'practice.clockHint': '练球开始即计时。',
  'practice.startFailed': '无法开始本次练习',
  'practice.saveFailed': '本次练习保存失败',

  'ranking.globalTitle': '全球评级榜',
  'ranking.button': '排行榜',
  'ranking.buttonHint': '直播对局评级与最高单杆',
  'ranking.seeAll': '查看全部排行榜',
  'ranking.empty': '还没有人进入评级。',
  'ranking.failed': '暂时无法获取排行榜。',

  'common.back': '返回',
  'common.loading': '加载中…',
  'common.pullToRefresh': '下拉刷新',
  'common.releaseToRefresh': '松开刷新',
  'common.cancel': '取消',
  'common.settings': '设置',
  'common.signOut': '退出登录',
  'common.signIn': '登录或注册',
  'common.fullscreenEnter': '全屏',
  'common.fullscreenExit': '退出全屏',
  'common.fullscreenExitNative': '按 F11 / Esc 退出',

  'seat.player': '球手 {n}',
  'seat.guestNumbered': '访客 {n}',
  'seat.guestName': '或以访客身份上场',
  'seat.guestHint': '访客对局不计入评级，也不计入技术统计。',
  'seat.guest': '访客',
  'seat.alreadySeated': '该账号已登录为球手 {n}，不能同时坐两个位置。',
  'profile.title': '账号设置',
  'profile.tabOverview': '概览',
  'profile.tabStats': '技术统计',
  'profile.tabDanger': '注销账号',
  'profile.tabEdit': '编辑资料',
  'profile.statsFailed': '技术统计加载失败',
  'profile.ranksFailed': '排名加载失败',
  'profile.rankings': '排名',
  'profile.practice': '练球',
  'profile.phoneTitle': '在自己的手机上修改',
  'profile.phoneHint': '扫码在手机上打开你的账号。需要在手机上用自己的密码登录，别人无法在这台设备上改动你的资料。',
  'profile.gateHint': '修改本账号的任何内容都需要再次输入密码。',
  'profile.password': '密码',
  'profile.unlock': '解锁',
  'profile.checking': '正在验证…',
  'profile.wrongPassword': '密码错误',
  'profile.firstName': '名',
  'profile.lastName': '姓',
  'profile.nationality': '国籍',
  'profile.club': '俱乐部',
  'profile.noClub': '无俱乐部',
  'profile.clubCountry': '俱乐部所在国家',
  'profile.clubCity': '俱乐部所在城市',
  'profile.avatar': '头像',
  'profile.save': '保存修改',
  'profile.saving': '正在保存…',
  'profile.saved': '已保存。',
  'profile.saveFailed': '保存失败',
  'profile.avatarFailed': '头像上传失败',
  'profile.dangerHint':
    '注销账号后你会在所有设备上退出登录，会员编号也会被释放。已经记录的比赛结果会保留，但你不会再出现在任何排行榜上。',
  'profile.typeToConfirm': '输入 {word} 以确认',
  'profile.close': '永久注销我的账号',
  'profile.closing': '正在注销…',
  'profile.closeFailed': '注销失败',

  'stat.rating': '评级',
  'stat.matchesPlayed': '比赛场数',
  'stat.matchesWon': '获胜场数',
  'stat.matchWinRate': '比赛胜率',
  'stat.framesPlayed': '总局数',
  'stat.framesWon': '获胜局数',
  'stat.frameWinRate': '单局胜率',
  'stat.pointsScored': '总得分',
  'stat.pointsConceded': '总失分',
  'stat.pointsPerFrame': '场均得分',
  'stat.potSuccessRate': '击球成功率',
  'stat.highestBreak': '单杆最高',
  'stat.totalBreaks': '有效单杆次数',
  'stat.averageBreak': '平均单杆',
  'stat.fifties': '50+ 单杆',
  'stat.centuries': '破百次数',
  'stat.foulsConceded': '犯规次数',
  'stat.pointsLostToFouls': '犯规送分',
  'stat.sessions': '练球次数',
  'stat.challengesAttempted': '挑战次数',
  'stat.challengesPassed': '通过挑战',
  'stat.progressScore': '进步得分',
  'stat.practicePoints': '练球得分',
  'stat.practiceHighestBreak': '练球单杆最高',
  'seat.idLabel': 'ID:',
  'seat.statFrames': '总局数',
  'seat.statWinRate': '胜率',
  'seat.statPotRate': '击球成功率',
  'seat.statHighBreak': '单杆最高',
  'seat.statAvgBreak': '平均单杆',

  'handicap.toggle': '让分',
  'reds.label': '红球数量',
  'match.redsDecrease': '减少一颗剩余红球',
  'scoreEntry.open': '给 {name} 加分',
  'scoreEntry.title': '加分 · {name}',
  'scoreEntry.amount': '增加分数',
  'scoreEntry.clear': '清空输入',
  'scoreEntry.delete': '删除最后一位',
  'scoreEntry.confirm': '确认加分',
  'scoreEntry.record': '增加 {n} 分',
  'match.redsIncrease': '增加一颗剩余红球',
  'match.redsAdjustHint': '调整台面红球（0–15 颗），不计分。进入清彩后须先撤销彩球得分才能恢复红球。',
  'reds.fewer': '减少红球',
  'reds.more': '增加红球',
  'handicap.off': '不让分。',
  'handicap.hint': '{name} 每局领先 {points} 分。',
  'handicap.gives': '{name} 让',
  'handicap.givesShort': '让',
  'handicap.less': '减少分数',
  'handicap.more': '增加分数',

  'match.title': '新建对战',
  'match.rated': '两个席位都已登录；所有计分及结束结果均在经确认的直播状态下记录，才计入评级。',
  'match.unrated': '登录后可保存个人统计；评级还需两位球手均登录，并在经确认的直播状态下计分。',
  'match.start': '开始对战',
  'match.starting': '正在开始…',

  'board.title': '排行榜',
  'board.liveOnly': '排行榜仅统计在直播已开启且经服务器确认时产生的数据。非直播数据及无法确认直播状态的历史记录不计入排名。',
  'board.rating': '评级',
  'board.ratingHint': 'Elo 评级要求双方均登录，且所有计分及结束结果都在直播状态下记录。',
  'board.highestBreak': '最高单杆',
  'board.highestBreakHint': '最高单杆只计算直播期间连续记录的得分，非直播部分不计入。',
  'board.practice': '练球',
  'board.practiceHint': '练习模式尚未关联可验证的直播，个人进度仍会保存，但不计入排行榜。',
  'board.city': '城市',
  'board.country': '国家',
  'board.global': '全球',
  'board.clubCity': '俱乐部所在城市',
  'board.needCountry': '请先选择国家，才能查看该榜单。',
  'board.needCity': '请先选择城市，才能查看该榜单。',
  'board.empty': '该范围内还没有人上榜。',
  'board.failed': '排行榜加载失败',
  'board.yourPlace': '我的排名',

  'frames.label': '局数',
  'frames.fewer': '减少一局',
  'frames.more': '增加一局',
  'frames.count': '局数',
  'frames.single': '单局定胜负',
  'frames.firstTo': '先胜 {n} 局',
  'frames.max': '最多 {n} 局',

  'signin.close': '关闭',
  'signin.tabSignIn': '登录',
  'signin.tabNew': '注册',
  'signin.username': '用户名',
  'signin.usernameHint': '你的名字 + # + 8 位会员编号，注册时已发送到你的邮箱。',
  'signin.email': '邮箱',
  'signin.emailHint': '用户名会发到这里，其他球手看不到你的邮箱。',
  'signin.password': '密码',
  'signin.signIn': '登录',
  'signin.signingIn': '正在登录…',
  'signin.firstName': '名',
  'signin.lastName': '姓',
  'signin.nationality': '国籍',
  'signin.club': '俱乐部（选填）',
  'signin.clubName': '俱乐部名称',
  'signin.clubCountry': '俱乐部所在国家',
  'signin.clubCity': '俱乐部所在城市',
  'signin.passwordHint': '至少 8 位，需同时包含字母和数字。',
  'signin.confirmPassword': '确认密码',
  'signin.mismatch': '两次输入的密码不一致。',
  'signin.avatar': '头像（选填）',
  'signin.avatarType': '这个文件不是图片',
  'signin.avatarSize': '头像图片大小不能超过 25 MB',

  'crop.title': '选择头像范围',
  'crop.hint': '拖动可移动，双指捏合或拖动滑块可缩放。',
  'crop.zoom': '缩放',
  'crop.cancel': '取消',
  'crop.use': '就用这块',
  'crop.again': '重新裁剪',
  'crop.loading': '正在打开图片…',
  'crop.failed': '这张图片无法打开，请换一张试试。',
  'crop.failedHeic':
    '这张 iPhone 照片（HEIC）无法读取。请另存为 JPG，或把 相机 → 格式 设为「兼容性最佳」。',
  'signin.create': '创建账号',
  'signin.creating': '正在创建…',
  'signin.failed': '出了点问题',

  'pair.description': '扫码即可在手机上登录或注册。席位会自动填好，本屏幕不需要输入任何密码。',
  'pair.alt': '登录配对二维码',
  'pair.short': '扫码用手机登录',
  'pair.forSeat': '扫码登录为{seat}',
  'pair.generating': '正在生成…',
  'pair.copy': '复制链接',
  'pair.copied': '链接已复制。',
  'pair.waiting': '等待中…该二维码有效期为 {n} 分钟。',
  'pair.expired': '二维码已过期，请重新生成。',
  'pair.newCode': '换一个二维码',
  'pair.failed': '二维码生成失败',
  'pair.retry': '重试',

  'join.title': '入座',
  'join.subtitle': '在这里注册或登录，记分板会自动为你安排席位。',
  'join.account': '你的账号',
  'join.failed': '入座失败',
  'join.success': '已入座',
  'join.seated': '{name} 已经入座。看一下记分板，那边已经更新了。',
  'join.idHint': '这是你的用户名，在任何设备上都能用它登录，我们也已经发到你的邮箱了。',
  'join.done': '完成',

  'toggle.on': '开',
  'toggle.off': '关',

  'ball.red': '红球',
  'ball.yellow': '黄球',
  'ball.green': '绿球',
  'ball.brown': '咖啡球',
  'ball.blue': '蓝球',
  'ball.pink': '粉球',
  'ball.black': '黑球',
  'ball.color': '彩球',

  'match.live': '在线',
  'match.offline': '离线',
  'match.reconnecting': '正在重连',
  'match.aiReferee': 'AI 裁判',
  'match.aiUnsupported': '此设备型号或授权不支持 AI 裁判',
  'match.aiDeviceChecking': '正在检查设备支持…',
  'match.aiDeviceCheckFailed': '无法确认设备支持，正在重试…',
  'match.stream': 'Streaming',
  'match.cameraNotFound': '找不到摄像头',
  'match.cameraStreamingNotReady': '请先在相机设置中配置 RTMP 地址和密钥',
  'match.frames': '局分',
  'match.bestOf': '{n} 局制',
  'match.high': '最高 {n}',
  'match.atTable': '该你击球',
  'match.handicapVerb': '让',
  'match.handicapPoints': '{n} 分',
  'match.highLabel': '最高单杆',
  'match.onLabel': '目标球',
  'match.gapLabel': '分差',
  'match.breakLabel': '当前单杆',
  'match.remainingLabel': '剩余可得',
  'match.redsLabel': '红球',
  'match.break': '单杆 {n}',
  'match.on': '目标球：{ball}',
  'match.remaining': '红球 {reds} \u00b7 剩余可得 {points}',
  'match.exhausted': '台面上已无分可得——请结束本局。',
  'match.foulAndMiss': '犯规且未解到（Foul and Miss）',
  'match.foulPoints': '罚分',
  'match.resetOverlay': '复位叠加（Reset overlay）',
  'match.resume': '继续（Resume）',
  'match.tablePreview': '桌面预览',
  'match.preShotReference': '击打前画面',
  'match.referenceUnavailable': '摄像机参考画面暂不可用',
  'match.restoreInstruction': '计分已暂停。打开复位叠加并手动摆球，完成后点击继续，仍由原选手击打。',
  'match.paused': '计分已暂停',
  'match.pausedWaitingRespot': '已暂停——等待彩球复位',
  'match.switchPlayer': '换手',
  'match.switchShort': '换手',
  'match.takeTable': '换 {name} 击球',
  'match.foul': '犯规 {n}',
  'match.foulLabel': '犯规',
  'match.freeBall': '自由球',
  'match.foulBackHint': '由 {name} 重新击打，犯规罚分保留。',
  'match.freeBallAction': '点击判定',
  'match.freeBallArmed': '自由球 —— 可指定任意球',
  'match.freeBallWorth': '指定球计 {n} 分',
  'match.undo': '撤销',
  'match.nothingToUndo': '当前没有可撤销的操作',
  'match.frameTied': '当前比分相同，请选择本局获胜的选手。',
  'match.saving': '正在保存…',
  'match.requestUnconfirmed': '请求超时，操作可能已保存。请刷新核对比分后再操作，避免重复记分。',
  'match.connectionUnconfirmed': '连接暂时中断，自动重试后仍未确认。请刷新核对比分和当前击球选手后再操作，避免重复加分或换手。',
  'match.endFrame': '结束本局',
  'match.whoWon': '这一局谁赢了？',
  'match.remake': '重打本局',
  'match.remakeConfirm': '重新开始本局？当前比分清零，且不计入局分。',
  'match.remakeGo': '确认重打',
  'match.cancel': '取消',
  'match.hasFrame': '{name} 已锁定本局',
  'match.unavailable': '该比赛不可用',
  'match.backToStart': '返回首页',
  'match.suggestions': 'AI 建议（{n}）',
  'match.accept': '接受',
  'match.reject': '拒绝',
  'match.suggestPot': '打进{ball}',
  'match.suggestPotMany': '打进 {n} \u00d7 {ball}',
  'match.suggestMiss': '未进球，换手',
  'match.suggestFoul': '犯规，{n} 分',

  'frame.over': '第 {n} 局结束',
  'frame.matchOver': '比赛结束',
  'frame.savedTitle': '比赛已保存',
  'frame.savedHint': '连续 20 分钟没有计分或比赛操作，比赛已自动关闭，当前比分已保存。',
  'frame.savedFrames': '第 {n} 局 · 局分 {a}–{b}',
  'frame.resumeOffline': '继续比赛不会自动开启直播或 AI，如有需要请单独开启。',
  'frame.resumeMatch': '继续比赛',
  'frame.takesFrame': '{name} 拿下本局',
  'frame.winsMatch': '{name} 赢得比赛',
  'frame.result': '第 {n} 局：{a}\u2013{b}',
  'frame.framesWord': '局',
  'frame.summary': '{n} 局制 \u00b7 先到 {m} 局',
  'frame.newMatch': '新比赛',
  'frame.startNext': '开始第 {n} 局',

  'remote.caption': '遥控器',
  'remote.useAsRemote': '用手机当遥控器',
  'remote.hint': '扫码即可用手机为这场比赛计分',
  'remote.alt': '遥控器二维码',
  'remote.drawing': '正在生成二维码…',
  'remote.failed': '无法生成二维码',
  'remote.title': '用手机计分',
  'remote.close': '关闭',
  'remote.description':
    '任何人扫码后都可为本场比赛计分、撤销和结束本局，包括第三方裁判，无需登录。二维码展开30秒后自动收起，已扫码的控制链接仍有效12小时，请仅分享给允许操作比分的人。',
  'remote.qrFailed': '二维码生成失败：{error}',
  'remote.useLink': '可以改用下面的链接——效果完全一样。',
  'remote.loopback':
    '未找到网络地址，这个链接只能在本机使用。请确认服务器正在运行，并且本机已连入场馆网络。',
  'remote.notConnected': '本设备未连接到服务器，另一台可能也没有连上。',
  'remote.connecting': '正在连接…',
  'remote.scanAgain': '请重新扫描记分牌上的二维码。',
  'remote.framesShort': '局分 {a}\u2013{b}',
  'remote.connected': '已连接',
  'remote.matchOver': '比赛已结束。',
  'remote.waitingNextFrame': '等待记分牌开始下一局。',
};

const MESSAGES: Record<Locale, Record<MessageKey, string>> = { en, de, zh };

function isLocale(value: string | null | undefined): value is Locale {
  return value != null && (LOCALES as readonly string[]).includes(value);
}

/**
 * Pick a starting language: whatever was chosen last, else whatever the
 * browser asks for, else English.
 *
 * Guessing from the browser matters because the first thing a player sees
 * should already be readable — being handed an English screen and told to go
 * find the language menu is exactly the friction this is meant to remove.
 */
function initialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  // A device that arrived by scanning the board's QR code is told which
  // language the board is set to, and that beats anything this phone knows
  // about itself: the two screens are being read by people standing together.
  const fromUrl = new URLSearchParams(window.location.search).get(LOCALE_PARAM);
  if (isLocale(fromUrl)) {
    try {
      window.localStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // Storage refused; the language still applies to this visit.
    }
    return fromUrl;
  }
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;
  for (const tag of navigator.languages ?? []) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return 'en';
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    // Screen readers and the browser's own hyphenation both key off this, and
    // it costs nothing to keep honest.
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A locked-down browser refusing storage is not a reason to refuse the
      // language change itself; it just will not survive a reload.
    }
  }, []);

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>): string => {
      const template = MESSAGES[locale][key] ?? en[key];
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
        name in vars ? String(vars[name]) : whole,
      );
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside a LocaleProvider');
  return ctx;
}

/** Shorthand for the common case of only needing to translate. */
export function useT(): LocaleContextValue['t'] {
  return useLocale().t;
}
